import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Flame, Trophy, TrendingUp, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to the AI News Community! 🎉',
    description: 'Your personalized hub for AI news, deep dives, and expert analysis.',
    icon: Sparkles,
  },
  {
    id: 'gamification',
    title: 'Track Your Learning Journey',
    description: 'Build reading streaks, unlock achievements, and compete on leaderboards as you stay informed.',
    icon: Flame,
  },
  {
    id: 'personalization',
    title: 'Get Personalized Recommendations',
    description: 'Our AI learns your interests and suggests content tailored just for you.',
    icon: TrendingUp,
  },
  {
    id: 'premium',
    title: 'Unlock Premium Features',
    description: 'Get exclusive deep dives, personalized feed, and weekly newsletters for deeper insights.',
    icon: Trophy,
  },
];

export const OnboardingFlow = () => {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkOnboarding();
  }, []);

  const checkOnboarding = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('id', user.id)
        .single();

      if (profile && !profile.onboarding_completed) {
        // Small delay for better UX
        setTimeout(() => {
          setOpen(true);
          setLoading(false);
        }, 1000);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking onboarding:', error);
      setLoading(false);
    }
  };

  const completeOnboarding = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);

      setOpen(false);
      toast.success('Welcome aboard! Start exploring AI news.');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const handleUpgrade = () => {
    completeOnboarding();
    navigate('/newsletter');
  };

  if (loading) return null;

  const step = ONBOARDING_STEPS[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <div className="space-y-6 py-4">
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Step {currentStep + 1} of {ONBOARDING_STEPS.length}</span>
              <button onClick={handleSkip} className="hover:text-foreground transition-colors">
                Skip
              </button>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Content */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-primary/10 p-4">
                <Icon className="h-8 w-8 text-primary" />
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold">{step.title}</h2>
              <p className="text-muted-foreground">{step.description}</p>
            </div>

            {/* Feature highlights for each step */}
            {step.id === 'gamification' && (
              <div className="grid grid-cols-3 gap-4 pt-4 text-xs">
                <div className="space-y-1">
                  <Flame className="h-4 w-4 mx-auto text-primary" />
                  <p className="text-muted-foreground">Daily Streaks</p>
                </div>
                <div className="space-y-1">
                  <Trophy className="h-4 w-4 mx-auto text-primary" />
                  <p className="text-muted-foreground">Achievements</p>
                </div>
                <div className="space-y-1">
                  <TrendingUp className="h-4 w-4 mx-auto text-primary" />
                  <p className="text-muted-foreground">Leaderboard</p>
                </div>
              </div>
            )}

            {step.id === 'premium' && (
              <div className="space-y-3 pt-4">
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-left">Exclusive deep dive analysis</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-left">AI-powered personalized feed</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-left">Weekly expert newsletter</span>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2">
            {step.id === 'premium' ? (
              <>
                <Button onClick={handleUpgrade} className="w-full" size="lg">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Upgrade to Premium
                </Button>
                <Button onClick={handleNext} variant="ghost" className="w-full">
                  Continue with Free
                </Button>
              </>
            ) : (
              <Button onClick={handleNext} className="w-full" size="lg">
                {isLastStep ? 'Get Started' : 'Next'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
